<template>
    <Modal size="md" @close="$emit('close')">
        <template #header>
            <div class="text-gray-600">Export to Zoho Mail</div>
        </template>

        <template #body>
            <div class="p-6">
                <form>
                    <SelectInput
                        v-model="state.account_id"
                        class="mb-6"
                        placeholder="Choose account"
                        :data="accounts"
                        label="Account"
                        required
                        :error="getError('account_id')"
                        :disabled="loading"
                        search
                        @blurred="v$.account_id.$touch()"
                    />
                </form>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between border-t border-gray-200 p-6">
                <AtomicButton color="white" size="md" @click="$emit('close')">
                    Cancel
                </AtomicButton>

                <AtomicButton
                    size="md"
                    color="yellow"
                    :disabled="loading || v$.$invalid"
                    @click="exportToZohoMail"
                >
                    Export
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { PropType, reactive, ref } from 'vue';
import { IIntegration, IZohoMail } from '@/types/Integrations/IIntegration';
import axios from 'axios';
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import ISelectable from '@/types/Atomic/ISelectable';

const emit = defineEmits(['close']);

const props = defineProps({
    integration: {
        required: true,
        type: Object as PropType<IIntegration<IZohoMail>>,
    },
    templateId: {
        required: true,
        type: Number,
    },
    templateName: {
        required: true,
        type: String,
    },
});

const state = reactive({
    account_id: '',
});

const rules = {
    account_id: {
        required,
    },
};

const v$ = useVuelidate(rules, state);

const loading = ref(false);
const accounts = ref<ISelectable[]>([]);

onMounted(() => {
    getAccounts();
});

const getAccounts = async () => {
    try {
        loading.value = true;
        const res = await axios.get(
            route('integrations.zoho-mail.accounts.show', {
                integration: props.integration.id,
            })
        );

        if (res.data.success === true) {
            accounts.value = res.data.data;
        }
    } catch (e) {
        return;
    } finally {
        loading.value = false;
    }
};

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || '';
};

const exportToZohoMail = async () => {
    loading.value = true;

    try {
        const response = await axios.post(
            route('templates.exports.zoho-mail', {
                template: props.templateId,
            }),
            {
                account_id: state.account_id,
                integration_id: props.integration.id,
            }
        );

        if (response.data.success) {
            create({
                title: 'Successful export',
                text: 'Template has been successfully exported to Zoho Mail.',
                type: 'success',
            });
            emit('close');

            return;
        }
        create({
            title: 'Unsuccessful export',
            text: 'Template has not been exported to Zoho Mail.',
            type: 'error',
        });
    } catch (e) {
        return;
    } finally {
        loading.value = false;
    }
};
</script>
